import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Divider,
    Grid,
} from '@material-ui/core/';
import { withStyles } from '@material-ui/core/styles';
import withWidth, { isWidthUp } from '@material-ui/core/withWidth';
import EnhancedTable from '../common/EnhancedTable/EnhancedTable';
import GridCard from '../common/GridCard/GridCard';
import HeaderWithTextInline from '../common/HeaderWithTextInline/HeaderWithTextInline';
import LineGraph from '../common/LineGraph/LineGraph';
import NoResultMessage from '../common/NoResultMessage/NoResultMessage';
import PageHeader from '../common/PageHeader/PageHeader';
import PrintButton from '../common/PrintButton/PrintButton';
import ReportDataHeader from '../common/ReportDataHeader/ReportDataHeader';
import ReportPage from '../common/ReportPage/ReportPage';
import routeConstants from '../../Routes/routeConstants';
import SearchStatsForm from './SearchStatsForm';
import trackingUtils from '../../utils/trackingUtils';
import searchStatsFormStatePropTypes from '../../proptypes/searchStatsFormStatePropTypes';
import suggestionsPropType from '../../proptypes/suggestionsPropType';
import translationPropType from '../../proptypes/translationPropType';

const styles = theme => ({
    root: {
        padding: theme.spacing(2),
        flexGrow: 1,
        maxWidth: '100%',
    },
    '@media print': {
        hidePrint: {
            display: 'none',
        },
    },
});

const SearchStatsWrapper = ({
    allSearches,
    allSearchesCount,
    classes,
    formState,
    graphData,
    isAdvancedUser,
    isLoading,
    noResult,
    searchActive,
    tableData,
    totalCountString,
    translation,
    width,
    ...formProps
}) => {
    useEffect(() => {
        trackingUtils.trackPageview(routeConstants.SEARCH_STATS);
    }, []);

    useEffect(() => {
        document.title = translation.SEARCH_STATS_PAGE_TITLE;
    }, [translation]);

    return (
        <div className={classes.root}>
            <Grid container spacing={2}>
                <GridCard xs={12} lg={10} xl={6} className={classes.hidePrint} cardOverflowVisible>
                    <PageHeader header={translation.SEARCH_WORDS} />
                    <SearchStatsForm
                        isAdvancedUser={isAdvancedUser}
                        searchActive={searchActive && !isLoading}
                        storeValues={formState}
                        translation={translation}
                        {...formProps}
                    />
                </GridCard>
                {searchActive && (
                    <Grid item xs={12} className={classes.hidePrint}>
                        <Divider />
                    </Grid>
                )}
                {noResult && (
                    <Grid item xs={12}>
                        <NoResultMessage translation={translation} />
                    </Grid>
                )}
                {!noResult && searchActive && (
                    <Grid item xs={12} className={classes.hidePrint}>
                        <PrintButton translation={translation} />
                    </Grid>
                )}
                {!noResult && graphData && (
                    <ReportPage printHeader={translation.SEARCH_STATISTICS}>
                        <Grid item xs={12}>
                            <HeaderWithTextInline
                                header={`${translation.PERIOD}: `}
                                text={`${formState.from} - ${formState.to}`}
                            />
                            <ReportDataHeader
                                data={totalCountString}
                                dataType={translation.SEARCHES}
                            />
                            {allSearches && (
                                <HeaderWithTextInline
                                    header={`${translation.SEARCHES}: `}
                                    text={
                                        allSearchesCount > 30
                                            ? `${allSearches.join(', ')} ${translation.AND.toLowerCase()} ${allSearchesCount - 30} ${translation.MORE.toLowerCase()}`
                                            : allSearches.join(', ')
                                    }
                                />
                            )}
                        </Grid>
                        <Grid container spacing={2}>
                            {isWidthUp('sm', width) && (
                                <Grid item xs={12}>
                                    <LineGraph
                                        graphData={graphData}
                                        isAdvancedUser={isAdvancedUser}
                                        translation={translation}
                                    />
                                </Grid>
                            )}
                            {tableData && (
                                <Grid item xs={12}>
                                    <EnhancedTable
                                        cols={tableData.dataCols}
                                        hideToolbar
                                        rows={tableData.dataRows}
                                        translation={translation}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </ReportPage>
                )
                }
            </Grid>
        </div>
    );
};

SearchStatsWrapper.propTypes = {
    allSearches: PropTypes.arrayOf(PropTypes.string),
    allSearchesCount: PropTypes.number.isRequired,
    classes: PropTypes.objectOf(PropTypes.any).isRequired,
    formState: searchStatsFormStatePropTypes.isRequired,
    graphData: PropTypes.objectOf(PropTypes.any),
    handleClear: PropTypes.func.isRequired,
    handleLogOut: PropTypes.func.isRequired,
    handleSelectWhatSuggestion: PropTypes.func.isRequired,
    handleSelectWhereSuggestion: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    handleUpdateFormField: PropTypes.func.isRequired,
    isAdvancedUser: PropTypes.bool.isRequired,
    isLoading: PropTypes.bool.isRequired,
    noResult: PropTypes.bool.isRequired,
    searchActive: PropTypes.bool.isRequired,
    selectedWhatSuggestions: suggestionsPropType.isRequired,
    selectedWhereSuggestions: suggestionsPropType.isRequired,
    tableData: PropTypes.objectOf(PropTypes.any),
    totalCountString: PropTypes.string,
    translation: translationPropType.isRequired,
    userLocale: PropTypes.string.isRequired,
    width: PropTypes.string.isRequired,
};
SearchStatsWrapper.defaultProps = {
    allSearches: null,
    graphData: null,
    tableData: null,
    totalCountString: null,
};

export default withWidth()(withStyles(styles)(SearchStatsWrapper));
